import React, { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "./ScrollEffects";

const stepKeys = ["step_1", "step_2", "step_3"] as const;
const stepEmojis = ["📝", "✨", "💬"];
const stepGradients = [
  "from-[hsl(25,85%,92%)] to-[hsl(35,90%,88%)]",
  "from-[hsl(40,90%,90%)] to-[hsl(50,95%,86%)]",
  "from-[hsl(15,80%,92%)] to-[hsl(25,85%,88%)]",
];

export const HowItWorksSection: React.FC = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });

  // Progress line animation tied to scroll
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0.8", "end 0.6"],
  });
  const lineProgress = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section className="py-28 sm:py-36 px-4 relative overflow-hidden" id="how-it-works" ref={sectionRef}>
      {/* Background */}
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse 70% 50% at 50% 60%, hsl(var(--primary) / 0.04) 0%, transparent 70%)",
      }} />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      <div className="max-w-5xl mx-auto relative">
        <ScrollReveal className="text-center mb-20">
          <span className="inline-block px-5 py-2 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-[0.15em] mb-6">
            {t('how_it_works.section_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground">
            {t('how_it_works.section_title_prefix')}{" "}
            <span className="text-primary">{t('how_it_works.section_title_highlight')}</span>
          </h2>
        </ScrollReveal>

        <div className="grid md:grid-cols-3 gap-10 sm:gap-14 max-w-4xl mx-auto relative">
          {/* Animated connecting line */}
          <div className="hidden md:block absolute top-[72px] left-[18%] right-[18%] h-[3px] rounded-full bg-primary/[0.07]">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-primary/40 via-primary/60 to-primary/40 origin-left"
              style={{ width: lineProgress }}
            />
            {/* Glowing dot at progress tip */}
            <motion.div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary/50"
              style={{
                left: lineProgress,
                boxShadow: "0 0 12px hsl(var(--primary) / 0.4)",
              }}
            />
          </div>

          {/* Mobile vertical connector */}
          <div className="md:hidden absolute top-0 bottom-0 left-1/2 w-px">
            <div className="h-full border-l-2 border-dashed border-primary/15" />
          </div>

          {stepKeys.map((key, i) => (
            <motion.div
              key={key}
              className="text-center relative z-10"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 + i * 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Emoji container with layered bg */}
              <div className="relative mx-auto mb-8 w-32 h-32">
                {/* Outer soft ring */}
                <motion.div
                  className={`absolute inset-0 rounded-[2rem] bg-gradient-to-br ${stepGradients[i]} opacity-60`}
                  animate={isInView ? { scale: [0.8, 1], opacity: [0, 0.6] } : {}}
                  transition={{ duration: 1, delay: 0.4 + i * 0.2 }}
                />
                {/* Inner card */}
                <motion.div
                  className="absolute inset-3 rounded-2xl bg-card/80 backdrop-blur-sm border border-primary/10 flex items-center justify-center"
                  style={{ boxShadow: "0 8px 32px -8px hsl(var(--primary) / 0.12)" }}
                  whileHover={{ scale: 1.08, rotate: [0, -4, 4, 0] }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                >
                  <span className="text-5xl">{stepEmojis[i]}</span>
                </motion.div>
              </div>

              {/* Step badge */}
              <motion.span
                className="inline-block px-4 py-1 rounded-full border border-primary/20 text-primary text-xs font-bold mb-4 bg-primary/5"
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ type: "spring", stiffness: 300, delay: 0.6 + i * 0.2 }}
              >
                {t('how_it_works.step_label')} {i + 1}
              </motion.span>

              <h3 className="font-black text-foreground text-xl mb-3">{t(`how_it_works.${key}.title`)}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-[260px] mx-auto">
                {t(`how_it_works.${key}.description`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
